import { useContext, useMemo } from 'react'
import { useRouter, type Href } from 'expo-router'
import { LanguageContext } from '../../i18n'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import {
  LANDING_MARKETING_HOME_PRICING_HREF,
  LANDING_SITE_NAV_ROUTES,
} from '../../landing/constants/landingSiteNav.constants'
import { ACCOUNT_ADMIN_PUBLIC_ROUTE } from '../constants/accountAdmin.constants'
import type {
  AccountAdminSiteMenuLink,
  AccountAdminSiteMenuLinksOverrides,
} from '../types/accountAdminNav.types'

/**
 * Marketing nav targets (Home, Organizations, …) for the org admin drawer.
 * Same links as the public landing nav; use wherever the drawer should offer site navigation.
 * Pass `overrides` on the marketing homepage so Home scrolls in-page; Contact uses `/contact`.
 */
export function useAccountAdminSiteMenuLinks(
  overrides?: AccountAdminSiteMenuLinksOverrides,
): AccountAdminSiteMenuLink[] {
  const router = useRouter()
  const { isSignedIn } = useSupabaseAuth()
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('useAccountAdminSiteMenuLinks must be used within LanguageProvider')
  }
  const { t } = languageContext

  return useMemo(
    () => [
      {
        key: 'home',
        label: t.siteNav.home,
        onPress: () => {
          if (overrides?.onHome) {
            overrides.onHome()
            return
          }
          router.push('/' as Href)
        },
      },
      {
        key: 'organizations',
        label: t.siteNav.organizations,
        onPress: () => {
          if (isSignedIn) {
            router.push(ACCOUNT_ADMIN_PUBLIC_ROUTE as Href)
          } else {
            router.push(LANDING_SITE_NAV_ROUTES.ORGANIZATIONS as Href)
          }
        },
      },
      {
        key: 'contact',
        label: t.siteNav.contact,
        onPress: () => {
          if (overrides?.onContact) {
            overrides.onContact()
            return
          }
          router.push(LANDING_SITE_NAV_ROUTES.CONTACT as Href)
        },
      },
      {
        key: 'packs',
        label: t.siteNav.packs,
        onPress: () => {
          if (overrides?.onPacks) {
            overrides.onPacks()
            return
          }
          router.push(LANDING_MARKETING_HOME_PRICING_HREF as Href)
        },
      },
    ],
    [router, t.siteNav, overrides?.onHome, overrides?.onContact, overrides?.onPacks, isSignedIn],
  )
}
